import { FunctionComponent, PropsWithChildren } from 'react'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Tooltip } from '~/components/Tooltip'
import { UpgradeChip } from '~/modules/monetization/upgrade/UpgradeChip'
import { UpgradeModal } from '~/modules/monetization/upgrade/UpgradeModal'
import { BIDDER_RULE_FORM_SECTION_LABELS, BidderRuleFormSection } from './bidderRuleSections'
import { BLOCKED_REASON, BLOCKED_SECTION_OFFER } from './blockedReason'

/**
 * The heading a rule section carries in the editor and the flyout alike: its label, greyed when the
 * account cannot edit the section, with the offer that would open it beside the label. `children` is the
 * right-hand control each surface adds — the bulk-selection links in the editor, the auto-add badge in
 * the flyout.
 *
 * A crown and the word "Upgrade", not a padlock and a statement of what is missing. Nothing here is
 * forbidden — the section is unbought, which is the one thing a lock does not say — and the same glyph
 * means the same thing on the ad unit tabs and on every catalog card. The whole label is the click, so a
 * reader who reads the line and reaches for it lands on the offer rather than on the icon alone; the
 * reason stays on hover, where it explains the offer instead of replacing it.
 */
export const BidderRuleSectionHeading: FunctionComponent<PropsWithChildren<{ section: BidderRuleFormSection; isBlocked: boolean }>> = ({ section, isBlocked, children }) => {
  const modal = useGlobalModal()
  const label = BIDDER_RULE_FORM_SECTION_LABELS[section]

  // `bare` hands the modal's surface to the card, so it is the same offer the Catalog and Ad units show.
  const showOffer = () => modal.open(UpgradeModal, { offers: [BLOCKED_SECTION_OFFER[section]], context: BLOCKED_REASON[section] }, { bare: true })

  return (
    <div className="flex items-center gap-2">
      {isBlocked ? (
        <Tooltip title={BLOCKED_REASON[section]}>
          <button
            type="button"
            onClick={showOffer}
            className="flex cursor-pointer items-center gap-2 border-none bg-transparent p-0 text-left"
            data-tracking-label="common-bidder-rules-upgrade-request"
          >
            <h3 className="font-bold text-slate-500">{label}</h3>
            <UpgradeChip>
              <span className="text-sm font-medium">{'Upgrade'}</span>
            </UpgradeChip>
          </button>
        </Tooltip>
      ) : (
        <h3 className="font-bold">{label}</h3>
      )}
      {children}
    </div>
  )
}
